import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Loader2, Globe, Store } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function RootSiteAffiliate() {
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState(null);
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState("payment_pending");
  const [busy, setBusy] = useState(false);

  const loadSettings = () => api.get("/root/site-affiliate/settings").then((r) => setSettings(r.data.settings)).catch((e) => toast.error(errMsg(e)));
  const loadOrders = (st = status) => api.get("/root/site-affiliate/orders", { params: st && st !== "all" ? { status: st } : {} }).then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  const loadReport = () => api.get("/root/site-affiliate/report").then((r) => setReport(r.data.report)).catch(() => setReport([]));
  useEffect(() => { loadSettings(); loadOrders(); loadReport(); }, []); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/root/site-affiliate/settings", settings); setSettings(data.settings); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const review = async (oid, action) => {
    const note = action === "approve" ? "" : (window.prompt(`${action === "reject" ? "Rejection" : "Refund"} reason:`) || "");
    if (action !== "approve" && !note) return;
    setBusy(true);
    try { await api.post(`/root/site-affiliate/orders/${oid}/review`, { action, note }); toast.success(`Order ${action}d`); loadOrders(); loadReport(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const S = (k) => ({ value: settings?.[k] ?? "", onChange: (e) => setSettings({ ...settings, [k]: e.target.value }) });

  return (
    <div className="space-y-6" data-testid="root-site-affiliate">
      <PageHeader title="Site Affiliate Program" subtitle="One product — a MILTROO business website (online store). Referral orders, pricing, commission & payment verification" testid="root-sa-title" />
      <Tabs defaultValue="orders">
        <TabsList data-testid="sa-admin-tabs">
          <TabsTrigger value="orders" data-testid="sa-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="report" data-testid="sa-tab-report">Creator Report</TabsTrigger>
          <TabsTrigger value="settings" data-testid="sa-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6 space-y-4">
          <Select value={status} onValueChange={(v) => { setStatus(v); setOrders(null); loadOrders(v); }}>
            <SelectTrigger data-testid="sa-status-filter" className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>{[["payment_pending", "Payment Verification Pending"], ["building", "Building (not paid)"], ["approved", "Approved"], ["rejected", "Rejected"], ["refunded", "Refunded"], ["all", "All"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {!orders ? <LoadingRows rows={3} /> : orders.length === 0 ? <EmptyState title="No orders" body="Direct website/store orders appear here." /> : (
            <TableShell head={["Business / Owner", "Type", "Price", "Payment Ref", "Referral", "Commission", "Status", "Actions"]} testid="sa-orders-admin">
              {orders.map((o) => (
                <TR key={o.id} testid={`sa-admin-order-${o.id}`}>
                  <TD><p className="font-semibold text-sm">{o.customer.business_name}</p><p className="text-[10px] text-muted-foreground">{o.customer.name} · {o.customer.email} · {o.customer.phone}</p></TD>
                  <TD><span className="text-xs font-bold capitalize flex items-center gap-1">{o.kind === "store" ? <Store className="h-3.5 w-3.5" /> : <Globe className="h-3.5 w-3.5" />}{o.kind}</span></TD>
                  <TD><span className="font-bold">{fmtINR(o.price)}</span></TD>
                  <TD><span className="text-xs font-mono">{o.payment?.reference || "—"}</span>{o.payment?.paid_at && <p className="text-[10px] text-muted-foreground">{o.payment.paid_at}</p>}</TD>
                  <TD>{o.creator_name ? <><p className="text-xs font-bold">{o.creator_name}</p><p className="text-[10px] text-muted-foreground">{o.ref_code}</p></> : <span className="text-xs text-muted-foreground">direct</span>}</TD>
                  <TD>{o.commission != null ? <><p className="text-xs font-bold text-emerald-600">{fmtINR(o.commission)}</p><p className="text-[10px] text-muted-foreground capitalize">{o.commission_status} · gross {fmtINR(o.company_gross)}</p></> : <span className="text-xs text-muted-foreground">—</span>}</TD>
                  <TD><StatusBadge status={o.status} />{o.admin_note && <p className="text-[10px] text-red-500 mt-1 max-w-[140px]">{o.admin_note}</p>}</TD>
                  <TD>
                    <div className="flex gap-1.5 flex-wrap">
                      {o.status === "payment_pending" && (
                        <>
                          <Button size="sm" data-testid={`sa-approve-${o.id}`} disabled={busy} onClick={() => review(o.id, "approve")} className="rounded-full font-bold text-white border-0" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                          <Button size="sm" variant="outline" data-testid={`sa-reject-${o.id}`} disabled={busy} onClick={() => review(o.id, "reject")} className="rounded-full font-bold text-red-500 border-red-500/40"><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                        </>
                      )}
                      {o.status === "approved" && (
                        <Button size="sm" variant="outline" data-testid={`sa-refund-${o.id}`} disabled={busy} onClick={() => review(o.id, "refund")} className="rounded-full font-bold text-amber-600 border-amber-500/40"><RotateCcw className="h-4 w-4 mr-1" />Refund</Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        <TabsContent value="report" className="mt-6">
          {!report ? <LoadingRows rows={3} /> : report.length === 0 ? <EmptyState title="No referral activity yet" /> : (
            <TableShell head={["Creator", "Code", "Orders", "Approved", "Commission Earned"]} testid="sa-report-table">
              {report.map((r) => (
                <TR key={r.creator_id} testid={`sa-report-${r.creator_id}`}>
                  <TD><p className="font-semibold text-sm">{r.creator_name}</p><p className="text-[10px] text-muted-foreground">{r.creator_email}</p></TD>
                  <TD><span className="text-xs font-mono">{r.code}</span></TD>
                  <TD>{r.orders}</TD>
                  <TD>{r.approved_orders}</TD>
                  <TD><span className="font-bold text-emerald-600">{fmtINR(r.commission)}</span></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          {!settings ? <LoadingRows rows={2} /> : (
            <div className="max-w-2xl space-y-5" data-testid="sa-settings-panel">
              <div className="flex items-center justify-between rounded-xl border border-border p-4">
                <div><p className="font-bold text-sm">Direct Creation Enabled</p><p className="text-xs text-muted-foreground">Public /create flow — one unified website/store product</p></div>
                <Switch data-testid="sa-enabled" checked={!!settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label>Price (₹)</Label><Input data-testid="sa-price" type="number" {...S("price")} /></div>
                <div className="space-y-1.5"><Label>Creator Commission (₹)</Label><Input data-testid="sa-comm" type="number" {...S("commission")} /></div>
                <div className="space-y-1.5"><Label>Minimum Price (₹)</Label><Input data-testid="sa-min-price" type="number" {...S("min_price")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Payment Link (UPI / gateway page — shown to customers at checkout)</Label>
                <Input data-testid="sa-payment-link" placeholder="https://…" {...S("payment_link")} /></div>
              <div className="space-y-1.5"><Label>Payment Instructions</Label>
                <Textarea data-testid="sa-payment-instructions" rows={3} {...S("payment_instructions")} /></div>
              <Button data-testid="sa-save-settings" onClick={save} disabled={busy} className="rounded-full font-bold text-white border-0" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
              </Button>
              <p className="text-[11px] text-muted-foreground">Prices must be ≥ minimum price; commissions must be ≤ order price. Prices/commissions are always charged from these server-side settings — customers can never modify them.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
